import { matchesFilter, type PlanFilter } from './plans';
import type { DateKey } from './time';

/** Structural subset of the API's Task. */
export interface TaskLike {
  id: string;
  title: string;
  member_id: string | null;
  due_date: DateKey | null;
  priority: 'high' | 'medium' | 'low';
  recurrence: 'none' | 'daily' | 'weekdays' | 'weekly';
  completed: boolean;
  completed_at: string | null;
  deleted_at?: string | null;
}

const PRIORITY_RANK = { high: 0, medium: 1, low: 2 } as const;

/** Due date (someday last), then priority, then title — stable for equal keys via id. */
export function compareTasks(a: TaskLike, b: TaskLike): number {
  if (a.due_date !== b.due_date) {
    if (a.due_date === null) return 1;
    if (b.due_date === null) return -1;
    return a.due_date < b.due_date ? -1 : 1;
  }
  const p = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
  if (p) return p;
  if (a.title !== b.title) return a.title < b.title ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export interface TaskGroups<T> {
  overdue: T[];
  today: T[];
  upcoming: { date: DateKey; tasks: T[] }[];
  someday: T[];
  /** Completed on the given local day (needs the completion date resolved by the caller). */
  doneToday: T[];
}

/**
 * Buckets for the Tasks screen. `completedOn` maps a completed task to its household-local
 * completion date (core stays time-zone agnostic; callers pass zonedParts).
 */
export function groupTasks<T extends TaskLike>(
  tasks: T[],
  today: DateKey,
  filter: PlanFilter,
  completedOn: (t: T) => DateKey | null,
): TaskGroups<T> {
  const g: TaskGroups<T> = { overdue: [], today: [], upcoming: [], someday: [], doneToday: [] };
  const byDate: Record<string, T[]> = {};
  const visible = tasks.filter((t) => !t.deleted_at && matchesFilter(t, filter)).sort(compareTasks);
  for (const t of visible) {
    if (t.completed) {
      if (completedOn(t) === today) g.doneToday.push(t);
      continue;
    }
    if (t.due_date === null) g.someday.push(t);
    else if (t.due_date < today) g.overdue.push(t);
    else if (t.due_date === today) g.today.push(t);
    else (byDate[t.due_date] = byDate[t.due_date] || []).push(t);
  }
  g.upcoming = Object.keys(byDate)
    .sort()
    .map((date) => ({ date, tasks: byDate[date]! }));
  return g;
}

/** Open tasks due today or earlier — the "left to do today" count. */
export function openDueCount(tasks: TaskLike[], today: DateKey): number {
  let n = 0;
  for (const t of tasks) if (!t.deleted_at && !t.completed && t.due_date !== null && t.due_date <= today) n++;
  return n;
}

export const RECURRENCE_LABEL: Record<TaskLike['recurrence'], string> = {
  none: 'Once',
  daily: 'Every day',
  weekdays: 'Weekdays',
  weekly: 'Every week',
};
